import { useRef } from "react";
import { Card, CardBody } from "@heroui/react";
import { DotsSixVerticalIcon } from "@phosphor-icons/react";
import { ICON_SIZE } from "@/config/constants";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  MeasuringStrategy,
} from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor, restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { TOOLBAR_ITEMS } from "@/features/notes/config";
import { ToolbarItem } from "@/features/notes/types";
import { Switch } from "@/shared/components/ui/Switch";
import { ToolbarItemSetting } from "@/services/api/fetchers/user-settings";

// Function to prevent scroll during drag
const preventScrollDuringDrag = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
};

// Measuring strategy to ensure proper element measurements during drag
const measuring = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

interface SortableItemProps {
  item: ToolbarItem;
  isEnabled: boolean;
  onToggle: (itemId: string) => void;
  isMobile: boolean;
}

function SortableToolbarItem({ item, isEnabled, onToggle, isMobile }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} onClick={() => onToggle(item.id)}>
      <Switch
        isSelected={isEnabled}
        label={item.label}
        description={item.description}
        startContent={item.icon}
        isDraggable
        dragIcon={!isMobile ? <DotsSixVerticalIcon size={ICON_SIZE.md} /> : undefined}
        dragHandleProps={!isMobile ? { ...attributes, ...listeners } : undefined}
        dragContentProps={isMobile ? { ...attributes, ...listeners } : undefined}
        onChange={() => onToggle(item.id)}
      />
    </div>
  );
}

interface ToolbarEditProps {
  className?: string;
  items: ToolbarItemSetting[];
  onToggle: (itemId: string) => void;
  onReorder: (items: ToolbarItemSetting[]) => void;
  isMobile: boolean;
}

export function ToolbarEdit({ className, items, onToggle, onReorder, isMobile }: ToolbarEditProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: isMobile ? { delay: 250, tolerance: 5 } : { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    resetState();
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        onReorder(reorderedItems);
      }
    }
  };

  const handleDragStart = () => {
    document.body.style.setProperty("cursor", "grabbing");

    // Additional mobile drag protection
    if (isMobile) {
      // Prevent any scroll events on the document during drag
      document.addEventListener("touchmove", preventScrollDuringDrag, { passive: false });
      document.addEventListener("scroll", preventScrollDuringDrag, { passive: false });
    }
  };

  const handleDragCancel = () => {
    resetState();
  };

  const resetState = () => {
    document.body.style.setProperty("cursor", "");

    // Clean up mobile drag protection
    if (isMobile) {
      document.removeEventListener("touchmove", preventScrollDuringDrag);
      document.removeEventListener("scroll", preventScrollDuringDrag);
    }
  };

  return (
    <Card ref={scrollContainerRef} className={className} style={{ touchAction: "none" }}>
      <CardBody className="gap-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor, restrictToParentElement]}
          measuring={measuring}
        >
          <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            {items.map((toolbarItemSetting) => {
              const toolbarItem = TOOLBAR_ITEMS.find((item) => item.id === toolbarItemSetting.id);
              if (!toolbarItem) return null;

              return (
                <SortableToolbarItem
                  key={toolbarItemSetting.id}
                  item={toolbarItem}
                  isEnabled={toolbarItemSetting.enabled}
                  onToggle={onToggle}
                  isMobile={isMobile}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </CardBody>
    </Card>
  );
}
